import React from 'react';

/**
 * Loading placeholders.
 *
 * The app used ~30 bare spinners floating in an empty page. A spinner tells you
 * "wait" and nothing else; a skeleton tells you what is coming and stops the
 * layout jumping when it arrives.
 *
 * `aria-hidden` + a single polite live region: a screen reader should hear
 * "Loading" once, not twelve shimmering boxes.
 */

export function Skeleton({ className = '' }) {
  return <div aria-hidden="true" className={`bg-muted animate-pulse ${className}`.trim()} />;
}

/** Rows that mirror a table, so the real table drops straight into place. */
export function TableSkeleton({ rows = 6, columns = 5, className = '' }) {
  return (
    <div className={className} role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">Loading…</span>
      <div aria-hidden="true">
        <div className="flex items-center gap-4 px-3 py-2.5 border-b border-border bg-muted/60">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} className={`h-3 ${i === 0 ? 'w-1/3' : 'flex-1'}`} />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-4 px-3 py-3.5 border-b border-border last:border-0">
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton
                key={c}
                className={`h-3.5 ${c === 0 ? 'w-1/3' : 'flex-1'}`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** For list/card surfaces (dashboards, queues). */
export function ListSkeleton({ rows = 4, className = '' }) {
  return (
    <div className={className} role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">Loading…</span>
      <div aria-hidden="true">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-start gap-3 px-4 py-3.5 border-b border-border last:border-0">
            <Skeleton className="h-8 w-8 rounded-md flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-1/3" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Whole-page fallback where a skeleton can't mirror anything meaningful. */
export function PageLoading({ label = 'Loading…' }) {
  return (
    <div className="flex items-center justify-center py-20" role="status" aria-live="polite" aria-busy="true">
      <div className="flex items-center gap-2.5 text-muted-foreground">
        <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" aria-hidden="true" />
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );
}

export default Skeleton;
